import { zodResolver } from "@hookform/resolvers/zod";
import { useId, useState } from "react";
import type { InputHTMLAttributes, Ref } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useAuth } from "../../app/AuthContext";
import { ApiError } from "../../shared/api/client";
import { Button, Field, InlineAlert, Input } from "../../shared/ui";
import { AuthShell } from "./AuthShell";

const schema = z.object({
  email: z.string().min(1, "Enter your email address").email("That does not look like an email"),
  password: z.string().min(1, "Enter your password"),
});

type FormValues = z.infer<typeof schema>;

/**
 * The password box and its reveal control.
 *
 * <p>Typing a password blind is where most failed sign-ins come from, and the people who need the
 * reveal most are the ones on a phone keyboard. It starts hidden and the button says which way it
 * is about to go, so nobody reveals a password by accident in an open-plan office.
 */
function PasswordInput({
  ref,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        className={`h-11 pr-11 ${className ?? ""}`}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((shown) => !shown)}
        aria-label={visible ? "Hide password" : "Show password"}
        className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

/**
 * Sign-in.
 *
 * <p>The failure messages are honest without being useful to an attacker. The server reports a
 * wrong password and an unknown address identically, so this page says the same thing for both
 * rather than inventing a distinction the API deliberately does not make.
 *
 * <p>A lockout is called out separately, because that one genuinely changes what the reader should
 * do: waiting will help, trying more passwords will not.
 */
export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const passwordId = useId();
  const passwordErrorId = `${passwordId}-message`;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await signIn(values.email, values.password);
      navigate("/", { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.code === "ACCOUNT_LOCKED") {
        setFormError(
          "This account is temporarily locked after repeated failed sign-ins. Wait a few " +
            "minutes and try again, or reset your password.",
        );
      } else if (error instanceof ApiError && error.code === "ACCOUNT_DISABLED") {
        setFormError("This account is not active. Contact an administrator.");
      } else if (error instanceof ApiError && error.status === 429) {
        setFormError("Too many attempts from this device. Wait a moment before trying again.");
      } else {
        setFormError("Email or password is incorrect.");
      }
    }
  });

  return (
    <AuthShell
      title="Sign in"
      subtitle="Use your company email address."
      footer="Can't get in? Ask your workspace administrator to check your account."
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <Field label="Email" error={errors.email?.message}>
          <Input
            type="email"
            autoComplete="username"
            autoFocus
            placeholder="you@huewine.com"
            className="h-11"
            {...register("email")}
          />
        </Field>

        {/* Not `Field`: the reset link belongs on the label line, where it is findable before the
            third failed attempt rather than after it. */}
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <label htmlFor={passwordId} className="text-sm font-medium text-ink-700">
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
            >
              Forgotten it?
            </Link>
          </div>
          <PasswordInput
            id={passwordId}
            autoComplete="current-password"
            aria-describedby={errors.password ? passwordErrorId : undefined}
            {...register("password")}
          />
          {errors.password?.message && (
            <p id={passwordErrorId} className="mt-1 text-xs text-red-600">
              {errors.password.message}
            </p>
          )}
        </div>

        <Button type="submit" loading={isSubmitting} className="h-11 w-full">
          {isSubmitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
